import { css, Divider, FormControl, NativeSelect, Stack, styled } from '@mui/material';
import { Fragment } from 'react';
import { useMatch, useNavigate, useParams } from 'react-router-dom';

import { NavigationExternalLink } from '@/components/navigations/NavigationExternalLink';
import { NavigationLink } from '@/components/navigations/NavigationLink';
import { useQueryOrganization } from '@/hooks/queries/useQueryOrganization';

const StyledSelect = styled(NativeSelect)(
  ({ theme }) => css`
    margin-right: ${theme.spacing(-2)};

    &::before,
    &::after {
      content: none;
    }

    .MuiNativeSelect-select {
      padding-bottom: 0;
    }
  `,
);

type SecondaryNavigationProps = {
  organizationSelect?: boolean;
  items: ({ label: string } & (
    | { to: string; end?: boolean; href?: undefined }
    | { to?: undefined; end?: undefined; href: string }
  ))[];
};

export function SecondaryNavigation({ organizationSelect = true, items }: SecondaryNavigationProps) {
  const { organizationId, locationId } = useParams();
  const navigate = useNavigate();
  const match = useMatch(`/arbeitgeber/${organizationId}/standorte/erstellen`);

  const { data: organization } = useQueryOrganization();

  return (
    <Stack
      direction="row"
      spacing={5}
      divider={
        <Divider
          sx={{ borderColor: 'text.primary', borderRightWidth: 2, height: 16, alignSelf: 'center' }}
          orientation="vertical"
          flexItem
        />
      }
    >
      {organizationSelect && (
        <Stack direction="row" spacing={5}>
          <FormControl>
            <StyledSelect
              size="small"
              value={`/arbeitgeber/${organizationId}${
                (locationId || match) && `/standorte/${locationId || 'erstellen'}`
              }`}
              aria-label="Arbeitgeber und Standorte"
              onChange={(event) => navigate(event.currentTarget.value)}
            >
              <option value={`/arbeitgeber/${organizationId}`}>{organization?.name}</option>

              {((organization?.locations && organization.locations.length > 0) || match) && (
                <optgroup label="Standorte">
                  {match && (
                    <option value={`/arbeitgeber/${organizationId}/standorte/erstellen`}>Standort erstellen</option>
                  )}

                  {organization?.locations?.map(({ id: locationItemId, name: locationName }) => (
                    <option key={locationItemId} value={`/arbeitgeber/${organizationId}/standorte/${locationItemId}`}>
                      {locationName}
                    </option>
                  ))}
                </optgroup>
              )}
            </StyledSelect>
          </FormControl>
        </Stack>
      )}
      {items.length > 0 && (
        <Stack direction="row" spacing={5}>
          {items.map(({ label, href, to, end = false }) => (
            <Fragment key={href || to}>
              {href && <NavigationExternalLink href={href}>{label}</NavigationExternalLink>}
              {to && (
                <NavigationLink to={to} end={end}>
                  {label}
                </NavigationLink>
              )}
            </Fragment>
          ))}
        </Stack>
      )}
    </Stack>
  );
}
